import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import api from "../../lib/api";

export default function DonateSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "polling", record: null, attempts: 0 });

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", record: null, attempts: 0 });
            return;
        }
        let cancelled = false;
        const poll = async (attempt) => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/donations/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState({ status: "paid", record: data, attempts: attempt });
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "expired", record: data, attempts: attempt });
                    return;
                }
                if (attempt >= 8) {
                    setState({ status: "pending", record: data, attempts: attempt });
                    return;
                }
                setTimeout(() => poll(attempt + 1), 2000);
            } catch {
                if (attempt >= 5) { setState({ status: "error", record: null, attempts: attempt }); return; }
                setTimeout(() => poll(attempt + 1), 2000);
            }
        };
        poll(0);
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="donate-success-page">
            {state.status === "polling" && (
                <>
                    <Loader2 size={48} className="text-primary mx-auto mb-6 animate-spin" />
                    <h1 className="font-display text-4xl mb-3">Confirming your gift…</h1>
                    <p className="text-muted-foreground">Just a moment while we confirm the payment with Stripe.</p>
                </>
            )}
            {state.status === "paid" && (
                <>
                    <CheckCircle2 size={56} className="text-secondary mx-auto mb-6" />
                    <h1 className="font-display text-5xl mb-3" data-testid="donate-success-title">Thank you, deeply.</h1>
                    <p className="text-lg text-muted-foreground mb-8">
                        Your gift{state.record?.amount ? ` of $${state.record.amount.toFixed(2)}` : ""} has reached us. It will fund climate resilience, peacebuilding, and humanitarian work across South Sudan.
                    </p>
                    <Link to="/" className="inline-flex items-center px-7 py-3.5 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium">Back to home</Link>
                </>
            )}
            {state.status === "expired" && (
                <>
                    <AlertCircle size={48} className="text-destructive mx-auto mb-6" />
                    <h1 className="font-display text-4xl mb-3">Session expired</h1>
                    <p className="text-muted-foreground mb-8">Your checkout session expired before payment was completed.</p>
                    <Link to="/donate" className="inline-flex items-center px-7 py-3.5 rounded-sm border border-primary text-primary text-sm font-medium">Try again</Link>
                </>
            )}
            {state.status === "pending" && (
                <>
                    <Loader2 size={48} className="text-muted-foreground mx-auto mb-6" />
                    <h1 className="font-display text-4xl mb-3">Payment processing</h1>
                    <p className="text-muted-foreground mb-8">Your payment is still being processed. You'll receive a confirmation by email shortly.</p>
                    <Link to="/" className="inline-flex items-center px-7 py-3.5 rounded-sm border border-primary text-primary text-sm font-medium">Back to home</Link>
                </>
            )}
            {state.status === "error" && (
                <>
                    <AlertCircle size={48} className="text-destructive mx-auto mb-6" />
                    <h1 className="font-display text-4xl mb-3">Something went wrong</h1>
                    <p className="text-muted-foreground mb-8">We couldn't confirm your donation. Please contact us if you were charged.</p>
                    <Link to="/contact" className="inline-flex items-center px-7 py-3.5 rounded-sm border border-primary text-primary text-sm font-medium">Contact us</Link>
                </>
            )}
        </div>
    );
}
